import Ship, { refillFireTokens } from './entities/Ship.js';
import Asteroid from './entities/Asteroid.js';
import Bullet from './entities/Bullet.js';
import {
  bulletAsteroidHitThisTick,
  collides,
  collidesWithRadius,
  circleCircleMarginSq,
  sweptCircleCollides,
  sweptMovingCircleCollides,
  wrap,
  applyShieldImpulseFromBullet,
  applyShieldImpulseFromAsteroid
} from './physics.js';
import { randomNumBetween, asteroidVertices, mulberry32 } from './helpers.js';
import { bulletSpawnKinematicsFromShip } from './entities/Bullet.js';
import {
  WORLD_WIDTH, WORLD_HEIGHT,
  BULLET_RADIUS,
  ASTEROID_VERTEX_COUNT, ASTEROID_START_SIZE, BULLET_LIFETIME_TICKS,
  SHIP_BURST_TOKENS_MAX,
  SHIELD_THICKNESS,
  TICK_MS
} from './constants.js'

function effectiveShipRadius(ship) {
  return ship.radius + (ship.hasShield() ? SHIELD_THICKNESS : 0)
}

/**
 * Hard cap on buffered inputs per ship. ~130 ms at 60 Hz; well beyond
 * normal jitter (single-pair CF batches = 2 inputs) but short enough that
 * catastrophic backpressure doesn't snowball.
 */
const INPUT_QUEUE_MAX = 8;

/** Ring buffer length (ticks) of ship + asteroid layouts for spawn-time lag checks. */
const LAG_COMP_HISTORY_TICKS = 64;
const LAG_COMP_MAX_REWIND_TICKS = 18;
/** Maps wall-clock delay (server receive − client fire) into rewind depth. */
const LAG_COMP_MS_TO_TICKS_SCALE = 0.55;
/** Floor so rewind depth is never zero when `t` is present (low RTT / skew-clamped delta). */
const LAG_COMP_ELAPSED_FLOOR_MS = TICK_MS * 0.35;

/** Stable order when one bullet can overlap several rocks same tick (Map order differs per runtime). */
function asteroidsSortedById(map) {
  return [...map.values()].sort((a, b) => a.id - b.id)
}

/**
 * Authoritative entity world. Owns ships/asteroids/bullets, runs fixed-dt
 * updates, resolves collisions, emits discrete events.
 *
 * Used directly on the server (ServerGame drives update + broadcasts events).
 * Client replay (`client/src/replayer/Playback.js`) builds the same tick order from decoded roster ids.
 */
export default class GameWorld {
  constructor({
    worldBounds = { width: WORLD_WIDTH, height: WORLD_HEIGHT, toroidalMap: true },
    mode = null,
    onEvent = () => { },
    now = () => Date.now(),
    matchSeed = null,
    replayPlayerOrder
  } = {}) {
    this.worldBounds = worldBounds;
    this.mode = mode;
    this.onEvent = onEvent;
    this.now = now;
    this._random = typeof matchSeed === 'number' ? mulberry32(matchSeed >>> 0) : null;
    this.replayPlayerOrder = [...replayPlayerOrder];
    this._tickInputRow = null
    this.ships = new Map();
    /** @type {Map<number, Asteroid>} */
    this.asteroids = new Map();
    /** @type {Map<number, Bullet>} */
    this.bullets = new Map();

    // Per-ship FIFO: one command popped per tick (matches client prediction under batching).
    /** @type {Map<string, Array<{keys:object, seq:number|undefined}>>} */
    this.inputQueues = new Map();

    this.nextAsteroidId = 1;
    this.nextBulletId = 1;
    this.tick = 0;

    // Per-tick ship + asteroid snap before rock integration (lag-comp rewind ring).
    /** @type {Array<{ tick: number, astById: Map<number, { x: number, y: number, radius: number, vx: number, vy: number }>, shipById: Map<string, { x: number, y: number, vx: number, vy: number, rotation: number }> }>} */
    this._lagCompFrameRing = [];
    this.postGameVisual = false
  }

  getShipTickOrder() {
    return this.replayPlayerOrder.filter(id => this.ships.has(id))
  }

  buildTickInputRow() {
    const out = [];
    for (const id of this.getShipTickOrder()) {
      const ship = this.ships.get(id);
      const q = this.inputQueues.get(id);
      if (!ship) {
        out.push({ left: false, right: false, up: false, down: false, firePresses: 0 });
        continue;
      }
      if (q && q.length && !this.postGameVisual) {
        const cmd = q[0];
        out.push({
          left: !!cmd.keys.left,
          right: !!cmd.keys.right,
          up: !!cmd.keys.up,
          down: !!cmd.keys.down,
          firePresses: Math.min(255, Math.max(0, Number(cmd.keys.firePresses) | 0))
        });
      } else {
        out.push({
          left: !!ship.input.left,
          right: !!ship.input.right,
          up: !!ship.input.up,
          down: !!ship.input.down,
          firePresses: Math.min(255, Math.max(0, Number(ship.input.firePresses) | 0))
        });
      }
    }
    return out;
  }

  getReplayTickRow() {
    const keys = this._tickInputRow
    const ids = this.getShipTickOrder()
    const out = []
    for (let i = 0; i < ids.length; i++) {
      const k = keys[i] ?? { left: false, right: false, up: false, down: false, firePresses: 0 }
      out.push(k)
    }
    return out
  }

  randomBetween(min, max) {
    if (this._random) return this._random() * (max - min + 1) + min;
    return randomNumBetween(min, max);
  }

  // ---- ship lifecycle -------------------------------------------------

  addShip(shipArgs) {
    const ship = new Ship(shipArgs);
    this.ships.set(ship.id, ship);
    return ship;
  }

  removeShip(id) {
    this.ships.delete(id);
    this.inputQueues.delete(id);
  }

  /**
   * Enqueue a user command. The command is consumed on the next tick of
   * that ship. If the transport batches, the queue backs up and each input
   * still gets exactly one physics tick; the oldest command is dropped if
   * the queue exceeds INPUT_QUEUE_MAX so memory stays bounded under
   * pathological batching.
   */
  setInput(shipId, keys, seq, clientTimeMs) {
    if (!this.ships.has(shipId)) return;
    let q = this.inputQueues.get(shipId);
    if (!q) {
      q = [];
      this.inputQueues.set(shipId, q);
    }
    const presses = Number(keys.firePresses);
    q.push({
      keys: {
        left: !!keys.left, right: !!keys.right,
        up: !!keys.up, down: !!keys.down,
        firePresses: Number.isFinite(presses) && presses > 0 ? Math.floor(presses) : 0
      },
      seq,
      clientTimeMs: typeof clientTimeMs === 'number' && Number.isFinite(clientTimeMs) ? clientTimeMs : undefined
    });
    while (q.length > INPUT_QUEUE_MAX) q.shift();
  }

  setPostGameVisual(enabled) {
    this.postGameVisual = !!enabled
  }

  setInvulnerable(ship, durationMs) {
    if (!ship) return
    ship.invulnerableUntil = durationMs > 0 ? this.now() + durationMs : 0
  }

  isInvulnerable(ship) {
    if (!ship) return false
    return Number.isFinite(ship.invulnerableUntil) && this.now() < ship.invulnerableUntil
  }

  respawnShip(id, position, { invulnerableMs = 0 } = {}) {
    const ship = this.ships.get(id);
    if (!ship) return;
    ship.alive = true;
    ship.position = { x: position.x, y: position.y };
    ship.velocity = { x: 0, y: 0 };
    ship.angularVelocity = 0;
    ship.rotation = position.rotation ?? 0;
    ship.fireTokens = SHIP_BURST_TOKENS_MAX;
    ship.lastTokenUpdateMs = this.now();
    if (ship.shieldMax > 0) ship.shieldHealth = ship.shieldMax;
    this.setInvulnerable(ship, invulnerableMs)
    this.onEvent({
      type: 'ship_respawn',
      playerId: id,
      x: position.x,
      y: position.y,
      rotation: ship.rotation,
      invulnerableUntil: ship.invulnerableUntil,
      tick: this.tick
    });
  }

  // ---- asteroid spawning ---------------------------------------------

  spawnAsteroid(opts) {
    const id = this.nextAsteroidId++;
    const vertices = opts.vertices ?? asteroidVertices(id, ASTEROID_VERTEX_COUNT, opts.radius);
    const rotationSpeed = opts.rotationSpeed ?? this.randomBetween(-1, 1);
    const a = new Asteroid({
      id,
      position: opts.position,
      velocity: opts.velocity ?? { x: this.randomBetween(-1.5, 1.5), y: this.randomBetween(-1.5, 1.5) },
      rotation: opts.rotation ?? 0,
      rotationSpeed,
      radius: opts.radius,
      vertices
    });
    this.asteroids.set(id, a);
    this.onEvent({
      type: 'spawn_asteroid',
      id, x: a.position.x, y: a.position.y,
      vx: a.velocity.x, vy: a.velocity.y,
      rotation: a.rotation, rotationSpeed: a.rotationSpeed,
      radius: a.radius, vertices: a.vertices, tick: this.tick
    });
    return a;
  }

  spawnAsteroidWave(count, size = ASTEROID_START_SIZE) {
    /** 
     * Spawn within a "play zone" around an active ship — far enough away to
     * not be an instant collision (>= 200 px), close enough to be discoverable
     * (<= 800 px). World wraps, so we pick a delta and add it to the ship.
     */
    const ships = [...this.ships.values()].filter(s => s.alive)
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
    for (let i = 0; i < count; i++) {
      let cx, cy;
      if (ships.length) {
        const s = ships[i % ships.length];
        cx = s.position.x; cy = s.position.y;
      } else {
        cx = this.worldBounds.width / 2; cy = this.worldBounds.height / 2;
      }
      const angle = this.randomBetween(0, Math.PI * 2);
      const dist = this.randomBetween(200, 800);
      let x = cx + Math.cos(angle) * dist;
      let y = cy + Math.sin(angle) * dist;
      if (this.worldBounds.toroidalMap === false) {
        x = Math.max(size, Math.min(this.worldBounds.width - size, x));
        y = Math.max(size, Math.min(this.worldBounds.height - size, y));
      } else {
        x = ((x % this.worldBounds.width) + this.worldBounds.width) % this.worldBounds.width;
        y = ((y % this.worldBounds.height) + this.worldBounds.height) % this.worldBounds.height;
      }
      this.spawnAsteroid({ position: { x, y }, radius: size });
    }
  }

  // ---- bullet spawning -----------------------------------------------

  spawnBulletFromShip(ship, clientFireTimeMs, inputSeq) {
    const align = this._lagCompFireAlignment(clientFireTimeMs, ship);
    const id = this.nextBulletId++;
    /**
     * Always use **post-update** `ship` for nose pose and velocity (matches client prediction).
     * Lag rewind is only for first-tick bullet↔rock checks against historical rock layout — rewinding
     * Rewinding ship `rotation` for spawn made spinning shots disagree with the client by several degrees.
     */
    const s = bulletSpawnKinematicsFromShip(ship);
    const b = new Bullet({
      id,
      ownerId: ship.id,
      position: { x: s.x, y: s.y },
      velocity: { x: s.vx, y: s.vy },
      rotation: s.rotation,
      fireInputSeq: typeof inputSeq === 'number' && Number.isFinite(inputSeq) ? inputSeq : undefined
    });
    this.bullets.set(id, b);
    this.onEvent({
      type: 'spawn_bullet', id, ownerId: ship.id,
      x: b.position.x, y: b.position.y, vx: b.velocity.x, vy: b.velocity.y,
      rotation: b.rotation, tick: this.tick,
      serverTime: this.now(),
      ...(typeof inputSeq === 'number' && Number.isFinite(inputSeq) ? { inputSeq } : {})
    });
    let hitAstId = null;
    if (align) {
      const prev = { x: b.position.x, y: b.position.y };
      const next = { x: b.position.x + b.velocity.x, y: b.position.y + b.velocity.y };
      if (this.worldBounds.toroidalMap === false) {
        next.x = Math.max(BULLET_RADIUS, Math.min(this.worldBounds.width - BULLET_RADIUS, next.x));
        next.y = Math.max(BULLET_RADIUS, Math.min(this.worldBounds.height - BULLET_RADIUS, next.y));
      } else {
        wrap(next, this.worldBounds.width, this.worldBounds.height, BULLET_RADIUS);
      }
      hitAstId = this._lagCompHitsFirstTick(align.frame, prev, next, BULLET_RADIUS);
    }
    if (hitAstId == null) hitAstId = this._instantOverlapSpawnHit(b);
    if (hitAstId != null) {
      this.destroyBullet(b.id, 'hit');
      this.destroyAsteroid(hitAstId, ship.id);
    }
    return b;
  }

  /** Nose spawn already overlapping an asteroid (same geometry client often sees). */
  _instantOverlapSpawnHit(bullet) {
    for (const ast of asteroidsSortedById(this.asteroids)) {
      if (collides(bullet, ast, this.worldBounds)) return ast.id;
    }
    return null;
  }

  /**
   * After ship step (including new bullet spawn), before asteroid integration: one ring row
   * per sim tick for lag-comp rewind.
   */
  _recordLagCompFrame() {
    const astById = new Map();
    for (const [id, a] of this.asteroids) {
      astById.set(id, {
        x: a.position.x,
        y: a.position.y,
        radius: a.radius,
        vx: a.velocity.x,
        vy: a.velocity.y
      });
    }
    const shipById = new Map();
    for (const [id, s] of this.ships) {
      shipById.set(id, {
        x: s.position.x,
        y: s.position.y,
        vx: s.velocity.x,
        vy: s.velocity.y,
        rotation: s.rotation
      });
    }
    this._lagCompFrameRing.push({ tick: this.tick, astById, shipById });
    while (this._lagCompFrameRing.length > LAG_COMP_HISTORY_TICKS) {
      this._lagCompFrameRing.shift();
    }
  }

  /**
   * Best frame with `tick <= targetTick` (latest such), so a missing exact tick still rewinds.
   */
  _lagCompFrameForRewindTick(targetTick) {
    let best = null;
    for (const e of this._lagCompFrameRing) {
      if (e.tick <= targetTick && (!best || e.tick > best.tick)) best = e;
    }
    return best;
  }

  /**
   * Historical asteroid layout for spawn-time lag checks. Bullet pose always uses live `ship`
   * after this tick’s update (matches client prediction).
   * @returns {{ frame: object, targetTick: number }|null}
   */
  _lagCompFireAlignment(clientFireTimeMs, _ship) {
    if (typeof clientFireTimeMs !== 'number' || !Number.isFinite(clientFireTimeMs)) return null;
    const now = this.now();
    const raw = now - clientFireTimeMs;
    if (!Number.isFinite(raw)) return null;
    const elapsedMs = Math.max(
      LAG_COMP_ELAPSED_FLOOR_MS,
      Math.min(1200, Math.max(0, raw))
    );
    const scaledTicks = (elapsedMs * LAG_COMP_MS_TO_TICKS_SCALE) / TICK_MS;
    const rawRewind = Math.max(1, Math.round(scaledTicks));
    const rewindTicks = Math.min(LAG_COMP_MAX_REWIND_TICKS, rawRewind);
    const targetTick = Math.max(1, this.tick - rewindTicks);
    const frame = this._lagCompFrameForRewindTick(targetTick);
    if (!frame || !frame.astById.size) return null;
    return { frame, targetTick };
  }

  /**
   * First-tick bullet↔rock sweep using historical rock layout `frame` (same as main sim collision).
   */
  _lagCompHitsFirstTick(frame, prev, next, br) {
    const dt = 1;
    for (const ast of asteroidsSortedById(this.asteroids)) {
      const rec = frame.astById.get(ast.id);
      if (!rec) continue;
      const astObjCurr = {
        position: { x: rec.x + rec.vx * dt, y: rec.y + rec.vy * dt },
        radius: rec.radius
      };
      const astPrevPos = { x: rec.x, y: rec.y };
      if (bulletAsteroidHitThisTick({
        bulletPrevPos: prev,
        bulletCurPos: next,
        bulletRadius: br,
        instantBulletPos: prev,
        instantAstPos: astPrevPos,
        astPrevPos,
        astCurPos: astObjCurr.position,
        astRadius: rec.radius,
        astPosForStaticSwept: astPrevPos
      }, this.worldBounds)) return ast.id;
    }
    return null;
  }

  // ---- tick -----------------------------------------------------------

  update(dt) {
    this._tickInputRow = this.buildTickInputRow()
    this.tick += 1;
    const now = this.now();
    const mode = this.mode;

    /**
     * One timeline per tick: ships (incl. bullet spawn + lag hit) → lag ring → asteroids →
     * bullets → collisions so authority ship, slug, and rocks stay mutually consistent.
     */

    /**
     * Ship physics + deferred fire. Consume one queued user command per
     * ship per tick; if none is queued (packet jitter / loss) the ship
     * keeps its previous input state — same repeat-last-command behavior
     * that authoritative servers use under packet loss.
     */
    for (const shipId of this.getShipTickOrder()) {
      const ship = this.ships.get(shipId);
      if (!ship) continue
      const q = this.inputQueues.get(ship.id);
      let consumedInputSeq;
      if (q && q.length && !this.postGameVisual) {
        const cmd = q.shift();
        ship.applyInput(cmd.keys);
        if (typeof cmd.seq === 'number') {
          ship.lastAckedSeq = cmd.seq;
          consumedInputSeq = cmd.seq;
        }
        if (typeof cmd.clientTimeMs === 'number' && Number.isFinite(cmd.clientTimeMs)) {
          ship.lastInputClientTimeMs = cmd.clientTimeMs;
        }
      }
      if (this.postGameVisual) {
        ship.applyInput({ left: false, right: false, up: false, down: false, firePresses: 0 })
      }
      void ship.update(dt, this.worldBounds);
      if (
        ship.alive
        && this.mode?.canShipFire?.(ship) !== false
        && !(
          this.mode?.disableFireDuringSpawnInvulnerability
          && this.isInvulnerable(ship)
        )
      ) {
        refillFireTokens(ship, now);
        let presses = ship.input.firePresses | 0;
        const inputSeqForSpawn = typeof consumedInputSeq === 'number'
          ? consumedInputSeq
          : ship.lastAckedSeq;
        while (presses-- > 0 && ship.fireTokens >= 1) {
          ship.fireTokens -= 1;
          ship.lastShotAt = now;
          this.spawnBulletFromShip(ship, ship.lastInputClientTimeMs, inputSeqForSpawn);
        }
      }
    }

    // After ships (and possible bullet spawn), before asteroid integration — lag-comp ring.
    this._recordLagCompFrame();

    const asteroidPrevPos = new Map();
    for (const a of this.asteroids.values()) {
      asteroidPrevPos.set(a.id, { x: a.position.x, y: a.position.y });
      a.update(dt, this.worldBounds);
    }
    const bulletPrevPos = new Map();
    const bulletExpiredThisTick = new Set();
    for (const b of this.bullets.values()) {
      const wasDead = b.dead;
      bulletPrevPos.set(b.id, { x: b.position.x, y: b.position.y });
      b.update(dt, this.worldBounds);
      if (!wasDead && b.dead && b.ageTicks >= BULLET_LIFETIME_TICKS) {
        bulletExpiredThisTick.add(b.id);
      }
    }

    // Collisions: bullet ↔ asteroid
    for (const bullet of [...this.bullets.values()]) {
      if (!this.bullets.has(bullet.id)) continue;
      const ttlExpiredThisTick = bulletExpiredThisTick.has(bullet.id);
      if (bullet.dead && !ttlExpiredThisTick) continue;
      for (const ast of asteroidsSortedById(this.asteroids)) {
        const prevPos = bulletPrevPos.get(bullet.id) ?? bullet.position
        const astPrevPos = asteroidPrevPos.get(ast.id) ?? ast.position
        if (bulletAsteroidHitThisTick({
          bulletPrevPos: prevPos,
          bulletCurPos: bullet.position,
          bulletRadius: bullet.radius,
          instantBulletPos: bullet.position,
          astPrevPos,
          astCurPos: ast.position,
          astRadius: ast.radius
        }, this.worldBounds)) {
          this.destroyBullet(bullet.id, 'hit');
          this.destroyAsteroid(ast.id, bullet.ownerId, { visualOnly: this.postGameVisual });
          break;
        }
      }
    }

    // Collisions: ship ↔ asteroid
    for (const ship of this.ships.values()) {
      if (!ship.alive) continue;
      const shipR = effectiveShipRadius(ship)
      for (const ast of asteroidsSortedById(this.asteroids)) {
        if (collidesWithRadius(ship, ast, shipR, ast.radius, this.worldBounds)) {
          if (!this.postGameVisual && this.canDamageShip(ship, null)) {
            if (ship.hasShield()) {
              applyShieldImpulseFromAsteroid(ship, ast, this.worldBounds)
              ship.shieldHealth = Math.max(0, ship.shieldHealth - 1)
              this.onEvent({
                type: 'ship_shielded',
                playerId: ship.id,
                x: ship.position.x,
                y: ship.position.y,
                hitBy: null,
                shieldHealth: ship.shieldHealth,
                shieldMax: ship.shieldMax,
                tick: this.tick
              })
            } else {
              this.hitShip(ship, null);
            }
          }
          this.destroyAsteroid(ast.id, null, { visualOnly: this.postGameVisual });
          break;
        }
      }
    }

    // Collisions: ship ↔ ship (mode-driven)
    if (mode?.shipVsShipCollision) {
      const ships = [...this.ships.values()].filter(ship => ship.alive)
      for (let i = 0; i < ships.length; i++) {
        for (let j = i + 1; j < ships.length; j++) {
          const shipA = ships[i]
          const shipB = ships[j]
          if (!shipA.alive || !shipB.alive) continue
          if (!collides(shipA, shipB, this.worldBounds)) continue
          if (this.isInvulnerable(shipA) || this.isInvulnerable(shipB)) continue
          if (this.postGameVisual) continue
          if (mode.onShipCollide) {
            mode.onShipCollide(this, shipA, shipB, mode.ctx)
          } else {
            this.hitShip(shipA, shipB.id)
            this.hitShip(shipB, shipA.id)
          }
        }
      }
    }

    // Collisions: bullet ↔ ship (mode decides friendliness)
    if (mode && (mode.shipVsShipCollision || mode.bulletHitsFriendly)) {
      for (const bullet of [...this.bullets.values()]) {
        if (bullet.dead) continue;
        for (const ship of this.ships.values()) {
          if (!ship.alive || ship.id === bullet.ownerId) continue;
          const ownerShip = this.ships.get(bullet.ownerId)
          if (
            !mode.bulletHitsFriendly
            && ownerShip?.teamId != null
            && ship.teamId != null
            && String(ownerShip.teamId) === String(ship.teamId)
          ) continue;
          if (this.isInvulnerable(ship)) continue
          if (collidesWithRadius(bullet, ship, bullet.radius, effectiveShipRadius(ship), this.worldBounds)) {
            this.destroyBullet(bullet.id);
            if (!this.postGameVisual && this.canDamageShip(ship, bullet.ownerId)) {
              if (ship.hasShield()) {
                applyShieldImpulseFromBullet(ship, bullet, this.worldBounds)
                ship.shieldHealth = Math.max(0, ship.shieldHealth - 1)
                this.onEvent({
                  type: 'ship_shielded',
                  playerId: ship.id,
                  x: ship.position.x,
                  y: ship.position.y,
                  hitBy: bullet.ownerId,
                  shieldHealth: ship.shieldHealth,
                  shieldMax: ship.shieldMax,
                  tick: this.tick
                })
              } else {
                this.hitShip(ship, bullet.ownerId);
              }
            }
            break;
          }
        }
      }
    }

    // Sweep dead bullets
    for (const [id, b] of [...this.bullets]) {
      if (!b.dead) continue;
      const ttlExpiredThisTick = bulletExpiredThisTick.has(id)
      if (ttlExpiredThisTick) {
        const prevPos = bulletPrevPos.get(id) ?? b.position
        let bestId = null
        let bestInstantMarginSq = Infinity
        let bestSweptMarginSq = Infinity
        let bestSweptRelativeMarginSq = Infinity
        for (const ast of asteroidsSortedById(this.asteroids)) {
          const instant = circleCircleMarginSq(b, ast, this.worldBounds)
          const swept = sweptCircleCollides(prevPos, b.position, b.radius, ast, this.worldBounds)
          const astPrevPos = asteroidPrevPos.get(ast.id) ?? ast.position
          const sweptRelative = sweptMovingCircleCollides(
            prevPos,
            b.position,
            b.radius,
            astPrevPos,
            ast.position,
            ast.radius,
            this.worldBounds
          )
          const localBest = Math.min(instant.marginSq, swept.marginSq, sweptRelative.marginSq)
          const globalBest = Math.min(bestInstantMarginSq, bestSweptMarginSq, bestSweptRelativeMarginSq)
          if (localBest < globalBest) {
            bestId = ast.id
            bestInstantMarginSq = instant.marginSq
            bestSweptMarginSq = swept.marginSq
            bestSweptRelativeMarginSq = sweptRelative.marginSq
          }
        }

        const bestCollisionMarginSq = Math.min(bestInstantMarginSq, bestSweptMarginSq, bestSweptRelativeMarginSq)
        if (bestId != null && bestCollisionMarginSq <= 0) {
          this.destroyBullet(id, 'ttl_overlap_recovered')
          this.destroyAsteroid(bestId, b.ownerId, { visualOnly: this.postGameVisual })
          continue
        }
      }
      this.destroyBullet(id, b.deadReason ?? 'ttl');
    }

    if (mode?.onTick) mode.onTick(this, this.tick, mode.ctx);

    // Wave refill
    if (!this.postGameVisual && this.asteroids.size === 0 && mode?.onAllAsteroidsCleared) {
      mode.onAllAsteroidsCleared(this, mode.ctx);
    }
  }

  // ---- destroy helpers -----------------------------------------------

  destroyAsteroid(id, byPlayerId, { visualOnly = false } = {}) {
    const ast = this.asteroids.get(id);
    if (!ast) return;
    this.asteroids.delete(id);

    let children = [];
    if (ast.canSplit()) {
      for (let i = 0; i < 2; i++) {
        const childRadius = ast.radius / 2;
        const pos = {
          x: ast.position.x + this.randomBetween(-10, 20),
          y: ast.position.y + this.randomBetween(-10, 20)
        };
        const vel = { x: this.randomBetween(-1.5, 1.5), y: this.randomBetween(-1.5, 1.5) };
        const child = this.spawnAsteroid({ position: pos, velocity: vel, radius: childRadius });
        children.push({
          id: child.id, x: child.position.x, y: child.position.y,
          vx: child.velocity.x, vy: child.velocity.y,
          rotation: child.rotation, rotationSpeed: child.rotationSpeed,
          radius: child.radius, vertices: child.vertices
        });
      }
    }

    this.onEvent({
      type: 'destroy_asteroid', id, destroyedBy: byPlayerId,
      scoreDelta: ast.score, x: ast.position.x, y: ast.position.y, radius: ast.radius,
      children, tick: this.tick
    });

    if (!visualOnly && this.mode?.onAsteroidDestroyed) {
      this.mode.onAsteroidDestroyed(this, ast, byPlayerId, this.mode.ctx);
    }
  }

  destroyBullet(id, reason = 'unknown') {
    const b = this.bullets.get(id);
    if (!b) return;
    this.bullets.delete(id);
    this.onEvent({ type: 'destroy_bullet', id, reason, tick: this.tick });
  }

  hitShip(ship, hitBy) {
    if (!ship.alive) return
    ship.alive = false;
    ship.velocity = { x: 0, y: 0 };
    ship.angularVelocity = 0;
    ship.input = { left: false, right: false, up: false, down: false, firePresses: 0 };
    this.onEvent({
      type: 'ship_hit', playerId: ship.id, x: ship.position.x, y: ship.position.y,
      hitBy, tick: this.tick
    });
    if (this.mode?.onShipHit) {
      this.mode.onShipHit(this, ship, hitBy, this.mode.ctx)
    }
  }

  canDamageShip(ship, sourceId) {
    if (!ship?.alive) return false
    if (this.mode?.canShipTakeDamage?.(ship, sourceId) === false) return false
    if (this.isInvulnerable(ship)) return false
    return true
  }

  // ---- snapshot ------------------------------------------------------

  getSnapshot(serverTime) {
    const modeState = this.mode?.getSnapshotState?.(this, this.mode.ctx) ?? null
    return {
      tick: this.tick,
      serverTime,
      ships: [...this.ships.values()].map(s => ({
        id: s.id, x: s.position.x, y: s.position.y,
        vx: s.velocity.x, vy: s.velocity.y, rotation: s.rotation, av: s.angularVelocity,
        color: s.color, name: s.name,
        teamId: s.teamId ?? null, teamName: s.teamName ?? null,
        alive: s.alive, lastAckedSeq: s.lastAckedSeq, thrusting: s.thrustingThisTick,
        invulnerableUntil: s.invulnerableUntil,
        lastShotAt: s.lastShotAt,
        fireTokens: s.fireTokens,
        lastTokenUpdateMs: s.lastTokenUpdateMs,
        respawnAt: s.respawnAt,
        shieldHealth: s.shieldHealth,
        shieldMax: s.shieldMax
      })),
      asteroids: [...this.asteroids.values()].map(a => ({
        id: a.id, x: a.position.x, y: a.position.y,
        vx: a.velocity.x, vy: a.velocity.y,
        rotation: a.rotation, rotationSpeed: a.rotationSpeed, radius: a.radius
      })),
      bullets: [...this.bullets.values()].map(b => ({
        id: b.id, ownerId: b.ownerId, x: b.position.x, y: b.position.y,
        vx: b.velocity.x, vy: b.velocity.y,
        ...(typeof b.fireInputSeq === 'number' && Number.isFinite(b.fireInputSeq)
          ? { inputSeq: b.fireInputSeq }
          : {})
      })),
      modeState
    };
  }
}
